import React, { useEffect, useState } from "react";

import { ElementState, Preview } from "@creatomate/preview";
import { SelectOption } from "@culturehq/components/dist/components/form/typings";

import { BuiltInFontDefinition } from "components/stories/video-studio/fonts/BuiltInFonts";
import FontFamily from "components/stories/video-studio/common/properties/text/FontFamily";
import FillColor from "components/stories/video-studio/common/properties/FillColor";
import FontSize from "components/stories/video-studio/common/properties/text/FontSize";
import FontStyle from "components/stories/video-studio/common/properties/text/FontStyle";
import FontWeight from "components/stories/video-studio/common/properties/text/FontWeight";
import { getOrganizationSelectOptionsFonts } from "components/stories/video-studio/utils";
import { isSubtitleText } from "../subtitle/utils";

interface TextFontValuesProps {
  activeElement: ElementState;
  organization: string;
  preview: Preview;
}

const TextFontValues: React.FC<TextFontValuesProps> = ({
  activeElement,
  organization,
  preview
}) => {
  const [parsedFonts, setParsedFonts] = useState<BuiltInFontDefinition[]>([]);
  const [selectedFont, setSelectedFont] = useState<BuiltInFontDefinition>();
  const [styles, setStyles] = useState<SelectOption[]>([]);
  const [weights, setWeights] = useState<SelectOption[]>([]);

  useEffect(
    () => {
      const getFontWeights = () => {
        const newFontValue = parsedFonts.find(
          parsedFont => parsedFont.family === activeElement.source.font_family
        );
        setWeights(newFontValue?.weights?.map(weight => ({ label: `${weight}`, value: `${weight}` })) || []);
      };

      const getFontStyles = () => {
        const newFontValue = parsedFonts.find(
          parsedFont => parsedFont.family === activeElement.source.font_family
        );
        setStyles(newFontValue?.styles?.map(style => ({ label: `${style.charAt(0).toUpperCase()}${style.slice(1)}`, value: style })) || []);
      };

      if (activeElement?.source?.type === "text"
        && !isSubtitleText(activeElement?.source?.name)
        && activeElement?.source?.font_family) {
        getFontWeights();
        getFontStyles();
      }
    }, [activeElement, parsedFonts]
  );

  useEffect(
    () => {
      const partialFonts = getOrganizationSelectOptionsFonts(organization, true);
      setParsedFonts(partialFonts);
      setSelectedFont(partialFonts[0]);
    }, [organization]
  );

  const handleFontChange = (value: BuiltInFontDefinition) => {
    setSelectedFont(value);
    setWeights(value.weights.map(weight => ({ label: `${weight}`, value: `${weight}` })) || []);
    setStyles(value.styles.map(style => ({ label: `${style.charAt(0).toUpperCase()}${style.slice(1)}`, value: style })) || []);
  };

  return (
    <>
      <div className="FontOptions">
        <FontFamily
          activeElement={activeElement}
          defaultValue={(organization === "Synopsys" || organization === "Synopsys Private Workspace") ? "Roboto" : undefined}
          onFontChange={handleFontChange}
          parsedFonts={parsedFonts}
          preview={preview}
        />
        <FillColor activeElement={activeElement} preview={preview} />
      </div>
      <div className="FontOptions-Secondary">
        <FontSize activeElement={activeElement} preview={preview} />
        <FontWeight
          activeElement={activeElement}
          preview={preview}
          selectedFont={selectedFont}
          weights={weights}
        />
        <FontStyle
          activeElement={activeElement}
          preview={preview}
          selectedFont={selectedFont}
          styles={styles}
        />
      </div>
    </>
  );
};

export default TextFontValues;
